// File: public/js/module-view.js

import { delegate } from "/js/shared/utils/delegate.js";

(() => {
  "use strict";

  const NAV_LIST_ID = "module-nav-list";
  const RIGHT_ID = "module-right";
  const ACTIVE_CLASS = "is-active";
  const STORAGE_KEY = "module_active_section";

  // Mobile "Chapitres" drawer (data-ui="toggle" + Elements::renderToggleSection).
  // Desktop never collapses (the class is inert there), so these only bite on
  // the mobile regime below.
  const DRAWER_ID = "module-nav-drawer";
  const COLLAPSE_CLASS = "module-nav-collapsed";
  const MOBILE_MQ = "(max-width: 1049px)";

  function getNavList() {
    return document.getElementById(NAV_LIST_ID);
  }

  function getRight() {
    return document.getElementById(RIGHT_ID);
  }

  function getItems() {
    const nav = getNavList();
    return nav ? Array.from(nav.querySelectorAll(".module-nav-item[data-module-section]")) : [];
  }

  function getSectionFromItem(li) {
    return li?.dataset?.moduleSection ? String(li.dataset.moduleSection) : "";
  }

  function saveActiveSection(section) {
    if (!section) return;
    try { sessionStorage.setItem(STORAGE_KEY, section); } catch (_) {}
  }

  function loadActiveSection() {
    try { return sessionStorage.getItem(STORAGE_KEY) || ""; } catch (_) { return ""; }
  }

  function clearActiveSection() {
    try { sessionStorage.removeItem(STORAGE_KEY); } catch (_) {}
  }

  function setActiveBySection(section) {
    if (!section) return false;

    const items = getItems();
    if (!items.length) return false;

    let found = false;
    for (const li of items) {
      const liSection = getSectionFromItem(li);
      const isTarget = liSection === section;

      li.classList.toggle(ACTIVE_CLASS, isTarget);
      if (isTarget) found = true;
    }
    return found;
  }

  function setIntroductionAsActive() {
    const items = getItems();

    const intro = items.find(li => getSectionFromItem(li) === "introduction");
    if (intro) {
      intro.classList.add(ACTIVE_CLASS);
      saveActiveSection("introduction");
      return;
    }

    const first = items[0];
    if (first) {
      const section = getSectionFromItem(first);
      if (section) {
        first.classList.add(ACTIVE_CLASS);
        saveActiveSection(section);
      }
    }
  }

  function setActiveFromDomOrStorage() {
    const items = getItems();

    const current = items.find(li => li.classList.contains(ACTIVE_CLASS));
    if (current) {
      const section = getSectionFromItem(current);
      if (section) saveActiveSection(section);
      return;
    }

    const stored = loadActiveSection();
    if (stored && setActiveBySection(stored)) return;

    setIntroductionAsActive();
  }

  function getFinalTestState() {
    const right = getRight();
    return right?.dataset?.finalTestState ? String(right.dataset.finalTestState) : "";
  }

  function isFinalTestRunning() {
    return getFinalTestState() === "running";
  }

  function setNavDisabled(disabled) {
    const items = getItems();

    for (const li of items) {
      const section = getSectionFromItem(li);
      const isValidation = section === "validation";

      // Only disable non-validation entries
      if (!isValidation) {
        li.classList.toggle("is-disabled", disabled);
      }
    }
  }

  function syncDisabledStateFromRight() {
    setNavDisabled(isFinalTestRunning());
  }

  function setupFinalTestAttestationGate() {
    const right = getRight();
    if (!right) return;

    // Only when the validation intro is displayed
    if (getFinalTestState() !== "ready") return;

    const form = right.querySelector('form[data-name="startModuleFinalTest"]');
    if (!form) return;

    const checkbox = form.querySelector('input[type="checkbox"][name="attestation"][value="1"]');
    if (!checkbox) return;

    const submitBtn = form.querySelector('button[type="submit"], input[type="submit"]');
    if (!submitBtn) return;

    const update = () => {
      const ok = !!checkbox.checked;
      submitBtn.disabled = !ok;
      submitBtn.classList.toggle("is-disabled", !ok);
      submitBtn.setAttribute("aria-disabled", ok ? "false" : "true");
    };

    // Initial state
    update();

    // Keep in sync
    checkbox.addEventListener("change", update);

    // Defense-in-depth: block submit if unchecked. Required message
    // travels via `data-attestation-required-message` so the literal
    // stays locale-aware (set server-side by ModuleFinalTestRenderer).
    form.addEventListener("submit", (e) => {
      if (!checkbox.checked) {
        e.preventDefault();
        e.stopPropagation();
        const requiredMsg = form.dataset.attestationRequiredMessage || "";
        if (requiredMsg !== "") {
          alert(requiredMsg);
        }
        update();
      }
    }, true);
  }

  // On mobile, collapse the "Chapitres" drawer after a chapter is picked so the
  // freshly-loaded content sits at the top. Mirrors the generic toggle's text
  // swap (set the trigger back to its "show" label) so the affordance stays
  // correct without re-clicking. No-op on desktop (the drawer is always open).
  function collapseDrawerOnMobile() {
    if (!window.matchMedia(MOBILE_MQ).matches) return;
    const drawer = document.getElementById(DRAWER_ID);
    if (!drawer || drawer.classList.contains(COLLAPSE_CLASS)) return;
    drawer.classList.add(COLLAPSE_CLASS);
    const trigger = document.querySelector(`[data-ui="toggle"][data-target="#${DRAWER_ID}"]`);
    if (trigger && trigger.dataset.textShow != null) {
      trigger.textContent = trigger.dataset.textShow;
    }
  }

  function onNavClick(li) {
    // UI guard
    if (li.classList.contains("is-disabled")) return;

    const section = getSectionFromItem(li);
    if (!section) return;

    setActiveBySection(section);
    saveActiveSection(section);
    collapseDrawerOnMobile();
  }

  function observeNavReplacement() {
    const nav = getNavList();
    if (!nav) return;

    const obs = new MutationObserver(() => {
      setActiveFromDomOrStorage();
      syncDisabledStateFromRight();
    });

    obs.observe(nav, { childList: true, subtree: true });
  }

  function observeRightReplacement() {
    const right = getRight();
    if (!right) return;

    const obs = new MutationObserver(() => {
      syncDisabledStateFromRight();
      setupFinalTestAttestationGate();
    });

    obs.observe(right, { childList: true, subtree: true, attributes: true });
    syncDisabledStateFromRight();
    setupFinalTestAttestationGate();
  }

  function handleHardReloadRule() {
    let isReload = false;

    try {
      const navEntries = performance.getEntriesByType?.("navigation") || [];
      if (navEntries.length && navEntries[0]?.type) {
        isReload = navEntries[0].type === "reload";
      } else if (performance.navigation) {
        isReload = performance.navigation.type === 1;
      }
    } catch (_) {}

    if (isReload) {
      clearActiveSection();
    }
  }

  function init() {
    handleHardReloadRule();
    delegate("click", ".module-nav-item[data-module-section]", onNavClick, { capture: true });

    // Always start on Introduction on initial paint
    setIntroductionAsActive();

    // Keep correct active state across AJAX nav refresh
    observeNavReplacement();

    // Disable nav during final test if backend renders running state
    observeRightReplacement();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();